import React from "react";
import {observer} from "mobx-react-lite";
import {Alert, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {CheckCircleOutline, HighlightOff} from "@mui/icons-material";
import {generatePath} from "react-router";
import {useStores} from "~/stores";
import {routes} from "~/routing/routes";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {taxonomyCanvasEditsStore} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasEditsStore.ts";
import {computeChangeSteps} from "~/prototypes/pages/taxonomy-canvas/approval/changeSteps.ts";
import {TaxonomyChangeList} from "~/prototypes/pages/taxonomy-canvas/approval/TaxonomyChangeList.tsx";
import {ConfirmActionDialog} from "~/prototypes/pages/taxonomy-canvas/approval/ConfirmActionDialog.tsx";

type Confirming = 'approve' | 'reject' | null;

/**
 * PROTOTYPE: review the pending Taxonomy Canvas edits, one row per change; open a row to see the affected
 * categories before and after the change. Revert single changes, or approve / reject all of them.
 * Everything happens in memory; nothing is sent to the backend.
 */
export const TaxonomyCanvasApprovalPage: React.FC = observer(() => {
    const {taxonomyManagerStore, bagStore} = useStores();
    const taxonomy = taxonomyManagerStore.taxonomy;
    const canvasPath = generatePath(routes.prototype_taxonomy_canvas, {id: String(bagStore.bagId)});

    const [confirming, setConfirming] = React.useState<Confirming>(null);
    const [lastResult, setLastResult] = React.useState<'approved' | 'rejected' | null>(null);

    const {approved, pending} = taxonomy ? taxonomyCanvasEditsStore.get(taxonomy.id) : {approved: [], pending: []};
    // Approved edits are the baseline; every pending edit becomes a step with its before / after tree
    const steps = React.useMemo(
        () => taxonomy ? computeChangeSteps(taxonomy.diff_tree, taxonomy.name, approved, pending) : [],
        [taxonomy, approved, pending]
    );

    const reject = (editKey: number) => {
        if (taxonomy) taxonomyCanvasEditsStore.revert(taxonomy.id, editKey);
    };
    const accept = (editKey: number) => {
        if (taxonomy) taxonomyCanvasEditsStore.approve(taxonomy.id, editKey);
    };
    const onConfirm = () => {
        if (!taxonomy || !confirming) return;
        if (confirming === 'approve') taxonomyCanvasEditsStore.approveAll(taxonomy.id);
        else taxonomyCanvasEditsStore.rejectAll(taxonomy.id);
        setLastResult(confirming === 'approve' ? 'approved' : 'rejected');
        setConfirming(null);
    };

    const n = pending.length;
    return <MainPageFacade
        title="Taxonomy Changes"
        titleTag="Prototype"
        back={canvasPath}
        backLabel="Canvas"
        actionBarEnd={<Stack direction="row" spacing={1.5}>
            <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff/>}
                disabled={n === 0}
                onClick={() => setConfirming('reject')}
            >
                Reject all
            </Button>
            <Button
                variant="contained"
                startIcon={<CheckCircleOutline/>}
                disabled={n === 0}
                onClick={() => setConfirming('approve')}
            >
                Approve all
            </Button>
        </Stack>}
    >
        {taxonomy === undefined && !taxonomyManagerStore.error &&
            <Stack alignItems="center" sx={{py: 6}}><CircularProgress/></Stack>}
        {taxonomyManagerStore.error && <Alert severity="error">Could not load the taxonomy.</Alert>}
        {taxonomy === null && <Alert severity="info">This dataset has no taxonomy.</Alert>}

        {taxonomy && <>
            {lastResult && n === 0 && <Alert severity={lastResult === 'approved' ? 'success' : 'info'} sx={{mb: 2}}>
                {lastResult === 'approved'
                    ? 'All changes approved. They are now part of the canvas (in memory only, nothing was saved).'
                    : 'All changes rejected. The canvas is back to the last approved state.'}
            </Alert>}
            {n === 0 && !lastResult && <Alert severity="info">
                {approved.length > 0
                    ? 'All changes have been reviewed. Accepted changes are part of the canvas (in memory only, nothing was saved).'
                    : 'There are no pending changes. Edit the taxonomy on the canvas first.'}
            </Alert>}
            {n > 0 && <>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {n} pending {n === 1 ? 'change' : 'changes'}, in the order they were made. Open a row to compare
                    before and after; accept or reject changes one by one, or all at once.
                </Typography>
                <TaxonomyChangeList steps={steps} onAccept={accept} onReject={reject}/>
            </>}
        </>}

        <ConfirmActionDialog
            open={confirming !== null}
            title={confirming === 'approve' ? 'Approve all changes' : 'Reject all changes'}
            message={confirming === 'approve'
                ? `Approve all ${n} pending ${n === 1 ? 'change' : 'changes'}? They become part of the canvas.`
                : `Reject all ${n} pending ${n === 1 ? 'change' : 'changes'}? They are undone on the canvas.`}
            confirmLabel={confirming === 'approve' ? 'Approve all' : 'Reject all'}
            confirmColor={confirming === 'approve' ? 'primary' : 'error'}
            onConfirm={onConfirm}
            onCancel={() => setConfirming(null)}
        />
    </MainPageFacade>
});
